/**
 * TanStack Query options for the conversations API. Follows andavo-dev's
 * separated query/mutation factory pattern (e.g. clients-query.ts): query keys +
 * queryFn/mutationFn live here; the hooks in useConversations.ts wrap them.
 * Every call goes through the vendored `api` wrapper (→ the resolver seam).
 */
import { api } from '@andavo/web-ui/api';
import type {
  Conversation,
  ConversationSummary,
  CreateConversationRequest,
  UpdateConversationRequest,
} from '@/features/chat/api-types';
import type { FeedbackRequest } from '@/features/chat/types';

const BASE = 'api/chat/conversations';

export const conversationQueries = {
  all: () => ['conversations'] as const,
  list: () => ({
    queryKey: [...conversationQueries.all(), 'list'] as const,
    queryFn: () => api.get<ConversationSummary[]>(BASE),
  }),
  detail: (id: string) => ({
    queryKey: [...conversationQueries.all(), 'detail', id] as const,
    queryFn: () =>
      api.get<Conversation>(`${BASE}?id=${encodeURIComponent(id)}`),
    enabled: Boolean(id),
  }),
};

export const conversationMutations = {
  create: () => ({
    mutationFn: (body: CreateConversationRequest) =>
      api.post<Conversation>(BASE, body),
  }),
  update: () => ({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: UpdateConversationRequest;
    }) => api.patch<Conversation>(`${BASE}?id=${encodeURIComponent(id)}`, body),
  }),
  remove: () => ({
    mutationFn: (id: string) =>
      api.delete<void>(`${BASE}?id=${encodeURIComponent(id)}`),
  }),
  /**
   * Submit thumbs-up/down feedback on an assistant message. The backend
   * (conversations.js ?action=feedback) opens a GitHub learning issue and
   * returns no useful body — so this is a fire-and-forget POST, not a query.
   */
  feedback: () => ({
    mutationFn: (body: FeedbackRequest) =>
      api.post<{ ok?: boolean }>(`${BASE}?action=feedback`, body),
  }),
};
